
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AttendanceStatus = 'present' | 'absent' | 'late';

export interface AttendanceRecord {
  id: string;
  teacherId: string;
  teacherName: string;
  date: string; // ISO string
  status: AttendanceStatus;
  checkInTime?: string;
  reason?: string;
  notes?: string;
  hasSubstitute?: boolean;
  substituteId?: string;
  substituteName?: string;
}

interface AttendanceState {
  attendanceRecords: AttendanceRecord[];
  getTeacherAttendance: (teacherId: string, date?: string) => AttendanceRecord | undefined;
  getDailyAttendance: (date?: string) => AttendanceRecord[];
  markAttendance: (record: Omit<AttendanceRecord, 'id'>) => void;
  updateAttendance: (id: string, updates: Partial<AttendanceRecord>) => void;
  assignSubstitute: (attendanceId: string, substituteId: string, substituteName: string) => void;
}

// Helper to get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export const useAttendanceStore = create<AttendanceState>()(
  persist(
    (set, get) => ({
      attendanceRecords: [],
      
      getTeacherAttendance: (teacherId, date = getTodayDateString()) => {
        return get().attendanceRecords.find(
          record => record.teacherId === teacherId && record.date.startsWith(date)
        );
      },
      
      getDailyAttendance: (date = getTodayDateString()) => {
        return get().attendanceRecords.filter(
          record => record.date.startsWith(date)
        );
      },
      
      markAttendance: (record) => {
        const id = `${record.teacherId}-${record.date}`;
        const existingIndex = get().attendanceRecords.findIndex(
          r => r.teacherId === record.teacherId && r.date.startsWith(record.date.split('T')[0])
        );
        
        set(state => {
          const newRecords = [...state.attendanceRecords];
          
          if (existingIndex >= 0) {
            // Update existing record
            newRecords[existingIndex] = { 
              ...newRecords[existingIndex],
              ...record,
              id: newRecords[existingIndex].id,
              checkInTime: record.checkInTime || new Date().toISOString()
            };
          } else {
            // Add new record
            newRecords.push({
              ...record,
              id,
              checkInTime: record.checkInTime || new Date().toISOString()
            });
          }
          
          return { attendanceRecords: newRecords };
        });
      },
      
      updateAttendance: (id, updates) => {
        set(state => ({
          attendanceRecords: state.attendanceRecords.map(record =>
            record.id === id ? { ...record, ...updates } : record
          )
        }));
      },
      
      assignSubstitute: (attendanceId, substituteId, substituteName) => {
        set(state => ({
          attendanceRecords: state.attendanceRecords.map(record =>
            record.id === attendanceId
              ? { 
                  ...record, 
                  hasSubstitute: true, 
                  substituteId, 
                  substituteName 
                }
              : record
          )
        }));
      }
    }),
    {
      name: 'teacher-attendance-storage',
      partialize: (state) => ({ attendanceRecords: state.attendanceRecords }),
    }
  )
);
